import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Modal } from '../primitives/Modal';
import { ProgressBar } from '../primitives/ProgressBar';
import { ResourceIcon } from './ResourceIcon';
import { RESOURCES, type ResourceKey } from '../../lib/game';
import { clamp01, formatAmount, formatExact, formatRate } from '../../lib/format';

/** One line of the "where does it come from" breakdown. */
export interface ResourceSource {
  label: string;
  /** Per-hour contribution. Negative values are drains. */
  perHour: number;
  /** Optional note, e.g. "Stufe 12" or "läuft mit 84 % Energie". */
  detail?: string;
}

export interface ResourceStatsProps {
  open: boolean;
  onClose: () => void;
  resource: ResourceKey;
  amount: number;
  /** Net per-hour rate — the number the top bar shows. */
  ratePerHour?: number;
  capacity?: number;
  /** Itemised contributions. They should sum to `ratePerHour`. */
  sources?: ResourceSource[];
  /** Free-form rows below the breakdown — energy balance, market rate, ... */
  footnotes?: ReactNode;
}

/**
 * The panel behind a resource in the top bar.
 *
 * A HUD counter answers "how much"; this answers the three questions that
 * follow and that a hover tooltip has no room for: where the rate comes from,
 * when the store fills up, and what is currently throttling it. Opened by
 * clicking the counter, because that is where the player's attention already is.
 */
export function ResourceStats({
  open,
  onClose,
  resource,
  amount,
  ratePerHour = 0,
  capacity,
  sources = [],
  footnotes,
}: ResourceStatsProps) {
  const meta = RESOURCES[resource];
  const ratio = capacity ? clamp01(amount / capacity) : 0;
  const full = capacity !== undefined && amount >= capacity;
  const headroom = capacity !== undefined ? capacity - amount : undefined;

  // Time to fill only means something while the store is actually filling.
  const hoursToFull =
    headroom !== undefined && headroom > 0 && ratePerHour > 0 ? headroom / ratePerHour : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      eyebrow="Rohstoff-Statistik"
      title={
        <span className="inline-flex items-center gap-2">
          <ResourceIcon resource={resource} size={20} />
          {meta.label}
        </span>
      }
    >
      <div className="rounded-control border border-edge-subtle bg-surface-2 px-3 py-3">
        <div data-sg-numeric className="text-readout font-semibold text-content">
          {formatExact(Math.floor(amount))}
          {capacity !== undefined ? (
            <span className="text-content-muted"> / {formatExact(capacity)}</span>
          ) : null}
        </div>
        {ratePerHour !== 0 ? (
          <div
            data-sg-numeric
            className={cn(
              'mt-0.5 text-xs font-semibold',
              ratePerHour < 0 ? 'text-danger-bright' : 'text-success-bright',
            )}
          >
            {formatRate(ratePerHour)}
          </div>
        ) : null}

        {capacity !== undefined ? (
          <>
            <ProgressBar
              className="mt-2.5"
              value={ratio}
              accent={full ? 'danger' : 'silicate'}
              size="xs"
              aria-label="Lagerauslastung"
            />
            <p className={cn('mt-1.5 text-xs', full ? 'text-warning-bright' : 'text-content-muted')}>
              {full
                ? 'Lager voll — die Förderung geht verloren. Baue das Lager aus.'
                : hoursToFull !== null
                  ? `Voll in ${formatHours(hoursToFull)} · noch ${formatAmount(headroom!)} frei`
                  : `Noch ${formatAmount(headroom ?? 0)} frei`}
            </p>
          </>
        ) : null}
      </div>

      {sources.length > 0 ? (
        <div className="mt-3">
          <h3 className="font-display text-label-sm uppercase text-content-muted">
            Produktion pro Stunde
          </h3>
          <dl className="mt-1.5 divide-y divide-edge-subtle rounded-control border border-edge-subtle bg-surface-2">
            {sources.map((source) => (
              <div key={source.label} className="flex items-baseline justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <dt className="truncate text-xs text-content">{source.label}</dt>
                  {source.detail ? (
                    <p className="text-label text-content-muted">{source.detail}</p>
                  ) : null}
                </div>
                <dd
                  data-sg-numeric
                  className={cn(
                    'shrink-0 text-xs font-semibold',
                    source.perHour < 0 ? 'text-danger-bright' : 'text-content-secondary',
                  )}
                >
                  {formatRate(source.perHour)}
                </dd>
              </div>
            ))}
            <div className="flex items-baseline justify-between gap-3 bg-surface-3 px-3 py-2">
              <dt className="font-display text-label uppercase text-content-muted">Gesamt</dt>
              <dd data-sg-numeric className="text-xs font-semibold text-content">
                {formatRate(ratePerHour)}
              </dd>
            </div>
          </dl>
        </div>
      ) : null}

      {footnotes ? <div className="mt-3 space-y-1.5">{footnotes}</div> : null}
    </Modal>
  );
}

/** "3,4 h" / "2,1 Tage" — coarse on purpose; this is a planning number. */
function formatHours(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${hours.toFixed(1).replace('.', ',')} h`;
  return `${(hours / 24).toFixed(1).replace('.', ',')} Tagen`;
}
